/* eslint-disable @typescript-eslint/no-empty-function */
import _useTranslation from 'lib/hooks/_useTranslation'
import { useEffect, useState } from 'react'
import { ClientOnly } from 'components/elements/ClientOnly'
import {
    AnalyticsEventUserCreate,
    AnalyticsEventWebappSignupDialogSuccessfulSignUp,
    AnalyticsEventSignupEmailBannerClick,
    AnalyticsEventPractisingElementSignUp,
} from 'lib/constants/AnalyticsEventType'
import { appFeedsRoute, appKnowRoute } from 'lib/constants/routes'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AdditionalSignUpAttributes, getDefaultAdditionalSignUpAttributes } from 'lib/tracking/attribution/signupattributes'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'
import { parseLocale } from 'lib/constants/localization'
import { getReferrerScreen } from 'lib/formatting/url'
import { Know } from 'interfaces/Know'
import PractisingElementBody from './PractisingElementBody'
import { Flashcard } from 'interfaces/Flashcard'
import styled from 'styled-components'
import FAQSchemaData from 'components/modules/SEO/FAQSchemaData'

const Container = styled.div`
    max-width: 1024px;
    overflow: hidden;
    border-radius: 30px;
    z-index: 1;
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
    @media (max-width: 750px) {
        padding: 0px;
    }
`

interface Props {
    know?: Know
    flashcards: Flashcard[]
    isDarkTheme?: boolean
    isMobile?: boolean
}

const PractisingElementModal = ({ know, flashcards, isDarkTheme, isMobile }: Props) => {
    const [additionalSignUpAttributes, setAdditionalSignUpAttributes] = useState<AdditionalSignUpAttributes>()
    const { t: tAuth } = _useTranslation('auth')
    const router = useRouter()
    const parsedLocale = parseLocale(router.locale)
    const referrerScreen = getReferrerScreen(router)

    useEffect(() => {
        const attributes = getDefaultAdditionalSignUpAttributes(
            parsedLocale.countryCode,
            parsedLocale.interfaceLanguageCode,
            know?.uuid
        )

        setAdditionalSignUpAttributes(attributes)
    }, [know])

    const bottomReferrerScreen = `know-preview-restriction-content-fixed-banner`

    const redirectAfterGoogleSignIn = () =>
        router.push(know ? appKnowRoute(know.uuid, { referrerScreen: bottomReferrerScreen }) : appFeedsRoute)

    const onSuccessSignUp = (isNewAccount: boolean, type: 'google' | 'apple' | 'email') => {
        if (isNewAccount) {
            trackEvent(AnalyticsEventUserCreate, { value: type, referrerScreen })
            toast.success(tAuth('auth/signUpRegisterSuccessMessage'))
        }

        trackEvent(AnalyticsEventWebappSignupDialogSuccessfulSignUp, { referrerScreen, know })
        trackEvent(AnalyticsEventPractisingElementSignUp, { referrerScreen, know })

        redirectAfterGoogleSignIn()
    }

    const onEmailButtonClick = () => trackEvent(AnalyticsEventSignupEmailBannerClick, { know, referrerScreen })

    const faqItems = flashcards.map((flashcard) => ({
        question: flashcard.questionText,
        answer: flashcard.answerText,
    }))

    return (
        <ClientOnly>
            <Container>
                {!isMobile ? <FAQSchemaData faqItems={faqItems} /> : null}
                <PractisingElementBody
                    additionalSignUpAttributes={additionalSignUpAttributes}
                    onSuccessSignup={onSuccessSignUp}
                    onEmailButtonClick={onEmailButtonClick}
                    know={know}
                    flashcards={flashcards}
                    isDarkTheme={isDarkTheme}
                />
            </Container>
        </ClientOnly>
    )
}

export default PractisingElementModal
